import angular from 'angular';
import { ImsObject } from 'app/shared/collections/ims_object';
import { LocalIMSObjectNotFound } from 'app/shared/collections/ims_object_collection_errors';
import {
    CollectionCRUDRunner,
    CRUDRunnerResult,
    CRUDRunnerResultCode,
    SimpleCRUDRunner,
} from 'app/shared/store_and_forward/CRUDRunner';
import { JobType } from 'app/shared/store_and_forward/StoreAndForwardJobStore';
import { unixNow, unixToDate } from 'app/shared/utilities/time';
import { StartupRegisterable } from 'app/startup/registry/registerable';
import { StartupRegistry } from 'app/startup/registry/registry';
import * as _ from 'lodash';
import { ChangeSetObj } from 'app/shared/components/bulk_edit/BulkEditService';
import DebugService from 'app/shared/debug.service';
import { EventRegistry } from 'app/shared/events/event_registry';
import { Metrics } from 'app/shared/utils';
import { ILocalStorageService } from 'angular-local-storage';

const log = (...args) => DebugService.anonymous('Collections', ...args);

const LAST_UPDATED_TIME_SECONDS_OFFSET = 30;

export interface CollectionCRUDResult<T extends ImsObject = ImsObject> {
    resultCode: CRUDRunnerResultCode;
    jobType: JobType;
    obj: T;
}

export interface ImsObjectCollectionOptions {
    refreshRate?: number;
    incremental?: boolean;
    onDemand?: boolean;
    runInit?: boolean;
    collectionEndPoint?: string;
    broadcastWhenEmpty?: boolean;
    crudRunner?: CollectionCRUDRunner;
    metrics?: Metrics;
}

export default class ImsObjectCollection<T extends ImsObject = any> extends StartupRegisterable {
    private readonly _metrics: Metrics;
    _$interval;
    _intervalPromise;
    all: Record<number, T>;
    refreshCount;
    lastUpdatedFromApi;
    // True when waiting for the first request to be returned from the API.
    // Can be set to true again at any point to force a wait for the next
    // successful request
    collectionNotInitialized = true;
    resourceObjectBase;
    imsObjectBase;
    refreshRate;
    incremental; // does the resource underneath this collection have a last_updated field to honour
    onDemand; // will this collection only refresh on request
    active;
    // Event registry for the Collection Changed event
    protected _collectionChangedEventRegistry: EventRegistry;
    private _onResetEventRegistry: EventRegistry;

    private _localToPersistentObjEventRegistry: EventRegistry;

    _listeners;

    private _collectionEndPoint: string;

    // Used to define the id of the next 'local' object that is added to this collection
    // Local objects are not saved to the API
    private _nextLocalObjectId: number = -1;

    // Whether or not the collection will still broadcast changes even when it has no data.
    // Note that this also affects whether or not a collection will return async data (e.g. getAllAsync) if it's empty.
    private _broadcastWhenEmpty = false;

    // Will be a pass through for most collections
    private _crudRunner: CollectionCRUDRunner;

    private _lastResourcesSize: number;
    private _lastChangedSize: number;

    private _localStorageService: ILocalStorageService;

    private _requestInFlight: boolean;

    /**
     *
     * @param startUpRegistry
     * @param localStorageService
     * @param $interval
     * @param resourceObjectBase
     * @param imsObjectBase
     * @param options a dict containing one or more of the following keys
     * Option: refreshRate
     * Option: incremental
     * Option: onDemand
     * Option: runInit Disables init on construction. Offers control over when initialise() is run.
     * Option: collectionEndPoint method called on the resource object base when refreshing / collecting new data
     * Option: broadcastWhenEmpty whether the changes should be broadcast even if there's no data
     * Defaults to false.
     */
    constructor(
        startUpRegistry: StartupRegistry,
        localStorageService: ILocalStorageService,
        $interval,
        resourceObjectBase,
        imsObjectBase,
        options: ImsObjectCollectionOptions = {},
    ) {
        super(startUpRegistry);

        const conformedOptions: ImsObjectCollectionOptions = Object.assign(
            {},
            {
                refreshRate: 600000, // 10 minutes
                incremental: false,
                onDemand: false,
                runInit: true,
                collectionEndPoint: 'query',
                broadcastWhenEmpty: false,
                crudRunner: null,
                metrics: new Metrics(window.performance),
            },
            options,
        );

        this._metrics = conformedOptions.metrics;
        if (!this._metrics) {
            throw new Error('collection metrics object is required');
        }

        // Most collections won't operation in store and forward mode so the SimpleCRUDRunner will be used
        // which maintains the old behaviour.
        this._crudRunner = conformedOptions.crudRunner
            ? conformedOptions.crudRunner
            : new SimpleCRUDRunner();

        this._collectionEndPoint = conformedOptions.collectionEndPoint;
        this._$interval = $interval;
        this._intervalPromise = null;
        this.all = {};
        this.refreshCount = 0;
        this.lastUpdatedFromApi = new Date();
        this.resourceObjectBase = resourceObjectBase;
        this.imsObjectBase = imsObjectBase;
        this.refreshRate = conformedOptions.refreshRate;
        this.incremental = conformedOptions.incremental; // TODO set these up properly
        this.onDemand = conformedOptions.onDemand;
        this.active = true; // TODO implement this
        this._broadcastWhenEmpty = conformedOptions.broadcastWhenEmpty;
        // Event registry for the Collection Changed event
        this._collectionChangedEventRegistry = new EventRegistry();
        this._localToPersistentObjEventRegistry = new EventRegistry();
        this._onResetEventRegistry = new EventRegistry();

        this._listeners = {};

        this._lastResourcesSize = 0;
        this._lastChangedSize = 0;

        this._localStorageService = localStorageService;
        this._requestInFlight = false;

        this._nextLocalObjectId = this._localStorageService.get(
            this._getLocalStorageNextLocalObjIdKey(),
        );
        if (!this._nextLocalObjectId) {
            this._nextLocalObjectId = -1;
        }

        if (this.onDemand) {
            // On demand collections are ready immediately - we don't need to wait for them for the
            // purposes of life cycle control
            // VERY IMPORTANT: notify the start up registry!
            this.setReady();
        }
    }

    start(): void {
        this.initialise();
    }

    /**
     * Return the ims type that this collection manages
     * @returns {string}
     */
    getImsType(): string {
        return new this.imsObjectBase().imsType;
    }

    /**
     * Add an event listener for when the collection is reset
     * @param {() => void} fn The callback to run
     * @param {boolean} runImmediately Optionally run the callback straight away, regardless of whether the collection
     * has already been reset prior or not.
     * @return {() => void} A destructor function for the event listener
     */
    addOnResetListener(fn: () => void, runImmediately: boolean = true): () => void {
        const rm = this._onResetEventRegistry.addEventListener(fn);
        if (runImmediately) {
            fn();
        }
        return rm;
    }

    /**
     * Add a collection on changed listener for this collection.
     * Wrapper for the adding root scope event listener
     * @param callbackFunc function of the form
     *
     * callbackFunc(changed, origin) {
     * }
     *
     * @param runImmediately Whether or not the function should be executed when registered
     *
     * @returns {*|(function())} the listener destroy function
     */
    addOnChangedListener(
        callbackFunc: (changed: { [key: string]: any }, origin: string) => void,
        runImmediately = true,
    ): () => void {
        const rm = this._collectionChangedEventRegistry.addEventListener(callbackFunc);

        // callback with the current collection AFTER it has been added to the registry in-case the listener is removed on the first callback
        if (runImmediately) {
            callbackFunc(this.all, undefined);
        }
        return rm;
    }

    /**
     * DANGER
     *
     * Before you use this think....do you REALLY need to use this?
     *
     * This allows something outside the collection to trigger the onChangedListeners.
     *
     * This has the potential to cause infinite loops. It's recommended
     * you protect the caller using the origin argument so at least you don't self-trigger.
     *
     * @param changed
     * @param origin
     */
    triggerOnChangedListeners(changed: { [key: number]: ImsObject }, origin: string) {
        this._emitChanged(changed, origin);
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.contains
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Returns true if the collection contains an item with the given id
     */
    contains(id: number): boolean {
        return id in this.all;
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.getAllLocalObjects
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Retrieves a filtered dict of all 'local' objects (id under 0).
     */
    getAllLocalObjects() {
        return _.pickBy(this.all, (obj) => {
            return obj.id < 0;
        });
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.getAsync
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Asynchronously retrieves an ims object via its id.
     *
     * First, attempt to retrieve the object straight from the collection.
     * If it's not there, and the collection is empty, wait for the collection to load.
     * If it's still not there, grab it directly from the api.
     *
     * Note the persist and processListeners default values are the same
     * as the default values for collectLatestImsObj().
     * The persist and processListeners arguments are only used if the id doesn't already
     * exist in the collection, i.e if it's deleted of if the collection is on demand.
     */
    async getAsync(id, persist = false, processListeners = false): Promise<T> {
        const collect = () => {
            return this.collectLatestImsObj(id, persist, processListeners);
        };
        const local = this.get(id);
        if (local) {
            return local;
        } else if (!this.onDemand && this.collectionNotInitialized) {
            return new Promise((resolve) => {
                const rmListener = this.addOnChangedListener(() => {
                    // The collection IS initialised. Proceed...
                    if (!this.collectionNotInitialized) {
                        const local = this.get(id);
                        if (local) {
                            resolve(local);
                        } else {
                            resolve(collect());
                        }
                        rmListener();
                    }
                }, false);
            });
        } else {
            return collect();
        }
    }

    /**
     * Promise resolves when collection transitions from NOT initialized to initialized
     * @returns {Promise<any>}
     */
    async waitForReadyCollection() {
        if (!this.collectionNotInitialized) {
            // We're ok!
            return Promise.resolve();
        } else {
            return new Promise((resolve) => {
                const rmListener = this.addOnChangedListener(() => {
                    // we have to check if the collection is initialized again because maybe the collection has just been reset;
                    // meaning you'll get a generic 'change' event which is actually the 'reset' event.
                    // So we actually want to wait for the first REAL change event to occur because saying getAllAsync is ready.
                    if (!this.collectionNotInitialized) {
                        resolve(null);
                        rmListener();
                    }
                }, false);
            });
        }
    }

    isRunning() {
        return !!this._intervalPromise;
    }

    /**
     * Starts the interval running.
     */
    startCollection() {
        if (!this.isRunning()) {
            this._intervalPromise = this._$interval(() => {
                this.refreshData({}, true, true);
            }, this.refreshRate);
        }
    }

    /**
     * Stops the interval from running.
     */
    stopCollection() {
        if (this.isRunning()) {
            this._$interval.cancel(this._intervalPromise);
            this._intervalPromise = null;
        }
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.get
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Retrieves an ims object via its id.
     */
    get(id): T | undefined {
        if (id < 0 && !(id in this.all)) {
            // id is less than 0 indicates a temporary, in mem object
            // and the ID does not exist in the object lookup, throw an error!
            throw new LocalIMSObjectNotFound(`${this.getImsType()}:${id} not found`);
        }
        return this.all[id];
    }

    safeGet(id: number): T | undefined {
        if (id > 0 && this.contains(id)) {
            return this.all[id];
        } else {
            console.error(`Asset ${id} does not exist in Asset Collection`);
            return undefined;
        }
    }

    public getAll(): Record<number, T> {
        return this.all;
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.getAllAsync
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Asynchronously returns all objects.
     *
     * First, attempt to retrieve all straight from the collection.
     * If the collection is empty, wait for the collection to load.
     * NOTE: Could take an indefinite amount of time to return if the collection is on-demand,
     * or an extremely long time if the collection has a large time-gap between refreshes.
     * Will only return on no data if broadcastWhenEmpty is true
     */
    async getAllAsync(): Promise<Record<number, T>> {
        const waitForCollection: () => Promise<Record<number, T>> = () => {
            return new Promise((resolve) => {
                const rmListener = this.addOnChangedListener((changed, origin) => {
                    // we have to check if the collection is initialized again because maybe the collection has just been reset;
                    // meaning you'll get a generic 'change' event which is actually the 'reset' event.
                    // So we actually want to wait for the first REAL change event to occur because saying getAllAsync is ready.
                    if (!this.collectionNotInitialized) {
                        resolve(this.all);
                        rmListener();
                    }
                }, false);
            });
        };

        if (!this.collectionNotInitialized) {
            return this.all;
        } else {
            // If on-demand, trigger refresh
            if (this.onDemand) {
                this.refreshData();
            }
            return waitForCollection();
        }
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.getAllAsArrayAsync
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Asynchronously returns the collection objects as an array.
     */
    async getAllAsArrayAsync() {
        return Promise.resolve(Object.values(await this.getAllAsync()));
    }

    /**
     * @ngdoc property
     * @name ImsObjectCollection.size
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * The number of objects in the collection.
     */
    get size() {
        return Object.keys(this.all).length;
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.getAllAsArray
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Returns the collection objects as an array.
     */
    getAllAsArray(): Array<T> {
        return Object.values(this.all);
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.getAllSelectableAsArray
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Returns the collection objects with property selectable=true as an array.
     */
    getAllSelectableAsArray(): Array<T> {
        return Object.values(this.all).filter(
            (a) => !a.hasOwnProperty('selectable') || a['selectable'],
        );
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.initialise
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Initialises the collection and retrieves initial data.
     * @returns Promise that resolves when refresh data is complete
     */
    initialise() {
        if (!this.resourceObjectBase) {
            console.error(
                this.constructor.name +
                    ' instantiated without setting resourceObjectBase. ' +
                    'This is a required parameter in ImsObjectCollection.',
            );
        }
        if (!this.imsObjectBase) {
            console.error(
                this.constructor.name +
                    ' instantiated without setting imsObjectBase. ' +
                    'This is a required parameter in ImsObjectCollection.',
            );
        }

        // If the collection is on demand, don't set up any refresh cycle
        if (this.onDemand) {
            return;
        }

        // Don't broadcast initial reset as there's nothing to do for anything listening in
        this.resetCollection(false);

        if (this.refreshRate) {
            this.startCollection();
        }

        return this.refreshData({}, false);
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.resetCollection
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Resets the objects collection.
     */
    resetCollection(broadcast = true) {
        this.all = {};
        if (broadcast) {
            this._emitChanged({}, this.constructor.name);
        }
        this._onResetEventRegistry.notifyListeners();
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.refreshData
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Retrieves an updated list of resources from the API and updates this collection.
     * @returns A promise that resolves
     */
    refreshData(params = {}, checkTime = false, useInFlightCheck = false) {
        // do nothing if there's already a request in-flight
        if (this._requestInFlight && useInFlightCheck) {
            return Promise.resolve({});
        }

        // okay we're about to be the request in-flight
        this._requestInFlight = true;

        // The limit should be enough to get EVERY row of interest of an non time series collection.
        // At the time of this comment, we ran into problems with integrations - a single site had over
        // 23000 integrations.
        const newParams = Object.assign({}, { limit: 100000 }, params);

        // We increment the refresh count because it's used by the incremental collection
        this.refreshCount++;

        // Calculate our next last updated timestamp to be right now subtract a few seconds to account for
        //  when the server is a little bit behind real time. In that situation; last_updated timestamps would be
        //  written in the past so the front-end would always miss those items.
        const nextLastUpdatedTime = unixToDate(unixNow() - LAST_UPDATED_TIME_SECONDS_OFFSET);

        return this.resourceObjectBase[this._collectionEndPoint](newParams)
            .$promise.then((resources) => {
                if (this.active) {
                    // set our last updated timestamp
                    this.lastUpdatedFromApi = nextLastUpdatedTime;

                    // get anything that changed (potentially nothing?)
                    const changed = this._filterInternal(
                        this._processResources(resources, checkTime),
                    );

                    this.collectionChanged(changed, this.constructor.name);

                    return changed;
                }

                return {};
            })
            .catch((error) => {
                throw createErrorFromRequestFailure('Error from refresh data', error);
            })
            .finally(() => {
                // request no longer in-flight
                this._requestInFlight = false;
            });
    }

    collectCount(params) {
        return new Promise((resolve, reject) => {
            this.resourceObjectBase.count(params).$promise.then(
                (response) => {
                    resolve(response);
                },
                (error) => {
                    reject(error);
                },
            );
        });
    }

    collectLatestImsObj(id, persist = false, processListeners = false) {
        const params = { limit: 1000, id };
        return this.resourceObjectBase
            .get(params)
            .$promise.then((resource) => {
                const changed = this._processResources([resource], false, true);

                if (processListeners && !persist) {
                    this.processListeners(changed);
                }
                if (persist) {
                    this.collectionChanged(changed, this.constructor.name); // This is the only place origin should be set, the actual origin of the change
                }

                return changed[id] || this.all[resource.id] || this.createImsObject(resource);
            })
            .catch((error) => {
                throw createErrorFromRequestFailure('Error from collect latest obj', error);
            });
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.collectData
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Retrieves an updated list of resources from the API based on the query parameters and returns them wrapped as ims objects.
     */
    collectData(params, persist = false, processListeners = false): Promise<{ [id: number]: T }> {
        const newParams = angular.extend({ limit: 1000 }, params);
        return new Promise((resolve, reject) => {
            this.resourceObjectBase[this._collectionEndPoint](newParams).$promise.then(
                (resources) => {
                    const changed = this._filterInternal(
                        this._processResources(resources, false, true),
                    );
                    if (processListeners && !persist) {
                        this.processListeners(changed);
                    }
                    if (persist) {
                        this.collectionChanged(changed, this.constructor.name); // This is the only place origin should be set, the actual origin of the change
                    }
                    resolve(changed);
                },
                (error) => {
                    reject(error);
                },
            );
        });
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.collectionChanged
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @param {array} [changed] The array of Ims Objects
     * @param {string} [origin] The name of the collection which initiated this change
     * @description
     * Called when collection changes.
     */
    collectionChanged(changed, origin: string): void {
        const _cct = window.performance.now();
        const changedSize = _.size(changed);

        const honourEmptyChanges =
            this.collectionNotInitialized || this.onDemand || !this.incremental;
        const hasChanged = changed && (changedSize != this._lastChangedSize || changedSize > 0);

        if (!honourEmptyChanges && !hasChanged) {
            return;
        }

        this._lastChangedSize = changedSize;

        // Mark the collection as changed
        this.collectionNotInitialized = false;

        // VERY IMPORTANT: notify the start up registry!
        this.setReady();

        /*
         Example 1. AssetCollection (this) just heard a change from IpDeviceCollection.
         AssetCollection just built it's new relationships with IpDeviceCollection
         Then AssetCollection called collectionChanged() on itself, with the origin of 'IpDeviceCollection'
         This collectionChanged() was only called because IpDeviceCollection changed, we don't need to process any listeners,
         that means we don't need to process any listeners here
         Say AssetCollection has 2 listeners, site_id and ip_devices.
         Don't process on site, as it hasn't changed (otherwise that would've been the origin)
         Don't process on ip_device again, as it was just processed before this function call.


         Example 2. AssetCollection (this) just changed, someone just changed a type
         We need to process each listener now to update the type field and (uselessly I know) the ip_devices too.
         Essentially refresh this one asset


         Example 3. ConditionCollection (this) just changed, someone added a new condition.
         This collection has a listener, but not for any other collection, it listens to itself essentially.
         So for each listener, check if the 'fromCollection' is undefined and if so, process an external foreign relationship between this.all and the changed objects
         */

        /////////////////////////////////
        //  Merge the changed objects  //
        /////////////////////////////////
        _.merge(this.all, changed);
        if (!this.incremental) {
            /*
             For non-incremental collections the 'changed' object should always be the current state of the collection
             In order to keep old references, merge in the changed object to this.all them remove anything that should not be in this.all any more.
             */
            this.all = _.pick(this.all, Object.keys(changed));
        }

        /*
         Delete any conditions in this.all that are marked as removed or is_deleted if incremental.
         Traverse through the CHANGED objects, if any object is is_deleted, mark it as removed in the changed objects (so collections can account for it in the emit)
         Delete it from the collection (if it exists in there)
         This saves traversing the entire collection each time, only the changed objects (which may be marked as removed, and hence changed)
         Delete from this.all so the change is reflected in the collection, and then emit after this.
         */
        const keys = Object.keys(changed);
        for (let i = 0; i < keys.length; i++) {
            if (changed[keys[i]].removed || changed[keys[i]].resource.is_deleted) {
                changed[keys[i]].removed = true;
                if (this.all[keys[i]]) {
                    delete this.all[keys[i]];
                }
            }
        }

        /////////////////////////////
        //  Process any listeners  //
        /////////////////////////////
        if (origin === this.constructor.name) {
            log(
                '%c' +
                    this.constructor.name +
                    '%c beginning change with %c' +
                    Object.keys(changed).length +
                    ' objects',
                'color: #009688',
                'color: black',
                'color: #009688',
            );
            this.processListeners(changed);
        } else {
            log(
                '%c\t' +
                    this.constructor.name +
                    '%c beginning change with %c' +
                    Object.keys(changed).length +
                    ' objects%c, and origin %c' +
                    origin,
                'color: #009688',
                'color: black',
                'color: #009688',
                'color: black',
                'color: #009688',
            );
        }

        /////////////////////
        //  Emit a change  //
        /////////////////////
        //Run before emit so receivers know it's already running/has run
        this._emitChanged(changed, origin);

        if (this.incremental) {
            /*
             The collection is incremental, add all the _last_asset_id fields to the ims object.
             These are used for faster processing.
             For example, the IpDevice collection changes it's asset_id
             When the change get's processed, by the AssetCollection,
             the IPDevice get's pulled directly from the old Asset (_last_asset_id)
             and put directly into the new asset (asset_id), no looping through collections at all.

             This has to be done AFTER the root scope emit, otherwise the _last_asset_id and asset_id will be the same.
             */
            _.forEach(this.all, (ims) => {
                _.forEach(ims.resource, (value, key) => {
                    if (key.endsWith('_id')) {
                        ims['_last_' + key] = value;
                    }
                });
            });
        }

        if (origin === this.constructor.name) {
            log(
                '%c' +
                    this.constructor.name +
                    '%c ending change (' +
                    (window.performance.now() - _cct).toFixed(3) +
                    'ms)',
                'color: #009688',
                'color: black',
            );
        } else {
            log(
                '%c\t' +
                    this.constructor.name +
                    '%c ending change with origin %c' +
                    origin +
                    '%c (' +
                    (window.performance.now() - _cct).toFixed(3) +
                    'ms)',
                'color: #009688',
                'color: black',
                'color: #009688',
                'color: black',
            );
        }
    }

    /**
     * Create a new IMS object. Overridable in child collections
     * @param resource
     * @param updateTime
     */
    createImsObject(resource, updateTime?) {
        return new this.imsObjectBase(resource, updateTime);
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection._processResources
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @param {object} [resources] The resources that should be traversed
     * @param {boolean} [checkTime = false] If the last_updated field in the resources should be compared
     * @param {boolean} [returnAllResources = false] Whether they should all be wrapped / updated and returned. Or only return the ones that were changed
     * @param {Date} [updateTime] The time to use for updating the resource, defaults to the collection last update
     * @returns {object} An object keyed by ID of all the changed IMS objects
     * @private
     * @description
     * Process any resources, wrap them in new ImsObjects or if they already exist in the collection, update the old one.
     * Sets all the ImsObjects' lastUpdatedFromApi field to the collections identical field
     */
    _processResources(
        resources,
        checkTime = false,
        returnAllResources = false,
        updateTime = this.lastUpdatedFromApi,
    ) {
        const resourcesSize = _.size(resources);

        const honourEmptyChanges =
            this.collectionNotInitialized || this.onDemand || !this.incremental;
        const hasChanged =
            resources && (resourcesSize != this._lastResourcesSize || resourcesSize > 0);

        if (!honourEmptyChanges && !hasChanged) {
            return {};
        }

        this._lastResourcesSize = resourcesSize;

        const changed = {};
        let resource = null;
        let imsObject = null;

        for (let i = 0; i < resources.length; i++) {
            resource = resources[i];
            imsObject = this.all[resource.id];

            // Create a new IMS Object
            if (!imsObject) {
                // Create and store new ims_object object if not exists
                imsObject = this.createImsObject(resource, updateTime);
                changed[imsObject.id] = imsObject;
                continue;
            }

            // Skip updating if it's already been done
            if (
                this.incremental &&
                checkTime &&
                !returnAllResources &&
                Date.parse(resource.last_updated) <= Date.parse(imsObject.resource.last_updated)
            ) {
                // If the last_updated time of the resource pulled is before the one currently in the collection skip over it
                continue;
            }
            // Update an existing IMS Object
            imsObject.applyResourceChange(resource);
            changed[imsObject.id] = imsObject;
        }

        return changed;
    }

    /**
     * @ngdoc method
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @param {object} [changed] The ims objects to process listeners with (an object keyed by ID)
     * @description
     * Runs through each listener in this collection and builds relationships based on the object provided
     */
    processListeners(changed) {
        const listenerKeys = Object.keys(this._listeners);
        for (let i = 0; i < listenerKeys.length; i++) {
            const listener = this._listeners[listenerKeys[i]];
            let touched = {};

            if (listener.fromCollection !== 'self') {
                // This collection has changed, but the listener collection is empty, so there's no relationships to be built
                if (!Object.keys(listener.fromCollection.all).length) {
                    continue;
                }

                log(
                    '%c\t' +
                        this.constructor.name +
                        '%c is building %c' +
                        Object.keys(changed).length +
                        '%c internal changes with %c' +
                        listener.fromCollection.constructor.name,
                    'color: #009688',
                    'color: black',
                    'color: #009688',
                    'color: black',
                    'color: #009688',
                );

                if (_.isNil(listener.internalForeignKey)) {
                    touched = this._buildExternalForiegnRelationship(
                        listener.toKey,
                        listener.externalForeignKey,
                        changed,
                        listener.fromCollection.all,
                    );
                } else {
                    const deleteRelationshipsOnChanged = true;
                    touched = this._buildInternalForeignRelationship(
                        listener.toKey,
                        listener.internalForeignKey,
                        changed,
                        listener.fromCollection.all,
                        deleteRelationshipsOnChanged,
                    );
                }
            } else {
                // Only for collections that listen to themselves. Nested relationships
                log(
                    '%c\t' +
                        this.constructor.name +
                        '%c is refreshing building %c' +
                        Object.keys(changed).length +
                        '%c internal changes with itself',
                    'color: #009688',
                    'color: black',
                    'color: #009688',
                    'color: black',
                );

                const alreadyHasItems = Object.keys(this.all).length > 0;
                const itemsToBuildWith = alreadyHasItems ? this.all : changed;
                touched = this._buildExternalForiegnRelationship(
                    listener.toKey,
                    listener.externalForeignKey,
                    itemsToBuildWith,
                    changed,
                );
            }
        }

        // Call a function everytime
        this.postListenerProcessing(changed);
    }

    /**
     * @ngdoc method
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @param {object} [changed] The ims objects that the listeners were processed with
     * @description
     * Runs after all the listeners get processing with the original changed objects. NOT the ones that any listener touched.
     */
    postListenerProcessing(changed) {
        /*  TODO Make this function run EACH time a listener is processed and pass through the name of the listener as well, and instead of 'changed' pass through 'touched'.
         That way any collection can add a callback to do something AFTER a listener was processed and it knows exactly what objects have been touched by the processing
         */
    }

    /**
     * Remove object with offline support
     *
     * @ngdoc method
     * @name ImsObjectCollection.removeObject
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Removes an IMS Object from the api and propagates those changes through the local collection
     */
    async removeObjectOffline(imsObject): Promise<CollectionCRUDResult> {
        if (imsObject.id >= 0) {
            const isDeleted = true;
            const resourceCopy = angular.copy(imsObject.resource);
            return this._crudAction(
                this._crudRunner.removeObject(imsObject),
                imsObject,
                resourceCopy,
                isDeleted,
            );
        } else {
            // signal to IMS widgets that this object needs to be removed
            imsObject.removed = true;
            // signal to the PersistedCache objects that this objet needs to be removed
            imsObject.resource.is_deleted = true;
            this._emitChanged({ [imsObject.id]: imsObject }, this.constructor.name);
            delete this.all[imsObject.id];
            return {
                jobType: JobType.REMOVE,
                resultCode: CRUDRunnerResultCode.REQUEST_COMPLETE,
                obj: imsObject,
            };
        }
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.removeObject
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Removes an IMS Object from the api and propagates those changes through the local collection
     */
    async removeObject(imsObject): Promise<any> {
        const result: CollectionCRUDResult = await this.removeObjectOffline(imsObject);
        return result.obj;
    }

    /**
     * Add an object with offline support
     *
     * @ngdoc method
     * @name ImsObjectCollection.addObject
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Adds an IMS Object into the api and propagates those changes through the local collection
     */
    async addObjectOffline(imsObject: T): Promise<CollectionCRUDResult<T>> {
        const resourceCopy = angular.copy(imsObject.resource);
        return this._crudAction(
            this._crudRunner.addObject(imsObject, this.getNextLocalObjId.bind(this)),
            imsObject,
            resourceCopy,
        );
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.addObject
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Adds an IMS Object into the api and propagates those changes through the local collection
     */
    async addObject(imsObject: T): Promise<T> {
        const result: CollectionCRUDResult<T> = await this.addObjectOffline(imsObject);
        return result.obj;
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.addLocalObject
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Adds a 'local' IMS Object into the collection but NOT the api. Sets its id to an auto-decrementing negative number
     */
    addLocalObject(imsObject) {
        imsObject.id = this.getNextLocalObjId();
        this.all[imsObject.id] = imsObject;
        return imsObject;
    }

    removeLocalObject(objId) {
        if (objId < 0) {
        } else {
            // do nothing
            console.error(
                `${this.getImsType()}: Called removeLocalObject() on a non local object: ${objId}`,
            );
        }
    }

    /**
     * Used to assign to local resources when saving ims objects in offline mode
     * @returns {number}
     */
    private getNextLocalObjId(): number {
        const nextId = this._nextLocalObjectId--;
        this._localStorageService.set(
            this._getLocalStorageNextLocalObjIdKey(),
            this._nextLocalObjectId,
        );
        return nextId;
    }

    /**
     * Update object with offline support
     *
     * The object must be a clone of the object that already exists in the collection. This enables
     * the collection to take a diff of the change that can be used for a 'safe' update to be
     * invoked some time in the future.
     *
     * @ngdoc method
     * @name ImsObjectCollection.updateObject
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Updates an IMS Object in the api and propagates those changes through the local collection
     */
    async updateObjectOffline(imsObject): Promise<CollectionCRUDResult<T>> {
        // If object is a non local object, or, the object is a local object but that
        // local object has been queued for a future write to the API...
        if (imsObject.id >= 0 || this._crudRunner.hasAddJobInQueue(imsObject)) {
            const existingObj = this.get(imsObject.id);
            if (Object.is(existingObj, imsObject)) {
                throw new Error(
                    'You must clone the object you want to update and make changes on the clone',
                );
            }
            const preRequestResource = angular.copy(imsObject.resource);
            return this._crudAction(
                this._crudRunner.updateObject(existingObj, imsObject),
                imsObject,
                preRequestResource,
            );
        } else {
            _.merge(this.all[imsObject.id].resource, imsObject.resource);
            return {
                jobType: JobType.UPDATE,
                resultCode: CRUDRunnerResultCode.REQUEST_COMPLETE,
                obj: imsObject,
            };
        }
    }

    /**
     * For parts of the application that don't need offline support (i.e. most of it the app),
     * unwrap the CollectionCRUDResult object.
     *
     * The imsObject cannot be the same reference that already exists in the collection. See updateObjectOffline().
     *
     * It's decided to make the caller do the cloning instead of magically cloning the object in
     * this method.
     *
     * Note that most forms already use / submit a clone because they implement dirty form checking.
     * For other edge cases where we don't do that it's ok to just clone the object that you want to
     * update upon calling this method
     *
     * @ngdoc method
     * @name ImsObjectCollection.updateObject
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Updates an IMS Object in the api and propagates those changes through the local collection
     */
    async updateObject(imsObject: any): Promise<any> {
        const result: CollectionCRUDResult = await this.updateObjectOffline(imsObject);
        return result.obj;
    }

    deleteBulk(imsList) {
        const formattedArray = [];
        _.forEach(imsList, (item) => {
            formattedArray.push(item.id);
        });

        //we pass deleted_ids through to the resource instead of ids so they don't get put in the query params.
        //We don't want this in case we bulk delete a shirt tonne of asset and hit the chrome URL limit
        return this._crudActionBulk(
            this.resourceObjectBase.deleteBulk({ deleted_ids: formattedArray.toString() }).$promise,
            imsList,
            true,
        );
    }

    /**
     * This is different to 'updateBulk' as it applies a single changeset to a large number of ids,
     * instead of sending a large number of individual changesets at once.
     * The changeset should be structured like:
     * {
     *     "ids": [],
     *     "type_id": 1,
     *     "site_id": 1,
     *     "add_tag_ids": [],
     *     "remove_tag_ids": [],
     *     "set_integrations": [{"system_id": 1, "key": "new_key", "active": True}] // Note, these are optional except system_id
     * }
     * @param changeset
     * @return {any}
     */
    applyBulkChanges(changeset: ChangeSetObj) {
        return this._crudActionBulk(
            this.resourceObjectBase.applyBulkChanges(changeset).$promise,
            null,
            false,
        ).then((response) => {
            this.refreshData();
        });
    }

    private _crudActionBulk(
        bulkFunction: Promise<T>,
        imsList: Array<ImsObject>,
        deleted: boolean,
        processResources: (
            resources: any,
            checkTime?: boolean,
            returnAllResources?: boolean,
            updateTime?: any,
        ) => any = this._processResources.bind(this), //can be mocked out to test changed resources
    ) {
        return bulkFunction
            .then((response) => {
                const changedObj = {};
                _.forEach(imsList, (imsObject) => {
                    // Process the response and run it through the collection
                    const resourceCopy = angular.copy(imsObject.resource);
                    _.defaults(imsObject.resource, Object.assign({}, resourceCopy));
                    const changed = processResources([imsObject.resource], false, true, new Date());
                    if (deleted) {
                        changed[resourceCopy.id].removed = true;
                    }
                    changedObj[imsObject.id] = changed[imsObject.id];
                });
                this.collectionChanged(changedObj, this.constructor.name);
                return changedObj;
            })
            .catch((error) => {
                throw createErrorFromRequestFailure(
                    'Error from crud operation _crudActionBulk',
                    error,
                );
            });
    }

    /**
     * This should be overridden by any collection that needs to filter out items based on criteria
     * specific to the collection.
     * @param items
     * @private
     */
    _filterInternal(items) {
        return items;
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.updateBulk
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * The bulk version of update.
     * Updates an array of IMS Objects in the api and propagates those changes through the local collection
     */
    public updateBulk(imsObjects, processResources = this._processResources.bind(this)) {
        const resources = [];

        // Check if this collection's resource supports this
        if (!this.resourceObjectBase.updateBulk) {
            throw new Error(
                "The collection's resource does not have an 'updateBulk' action defined",
            );
        }

        // Get the array of resources
        _.forEach(imsObjects, (imsObject) => {
            if (imsObject.resource) {
                resources.push(imsObject.resource);
            }
        });

        return this._crudActionBulk(
            this.resourceObjectBase.updateBulk(resources).$promise,
            imsObjects,
            false,
            processResources,
        );
    }

    async _crudAction(
        promise: Promise<CRUDRunnerResult>,
        sourceImsObject,
        preRequestResource,
        deleted = undefined,
    ) {
        try {
            const crudRunnerResult: CRUDRunnerResult = await promise;
            const resultResource = crudRunnerResult.resource;
            // Put back keys from the resource that the update serializer doesn't return
            // TODO Bug if a property changed from '123' to 'undefined', it'll be ignored here.. causing bugs!
            // temp solution, use NULL when changing properties of the ims object resource for now.
            // eg: {asset_id: 1} -> {asset_id: undefined} = this change will be ignored until page reload!!!
            _.defaults(sourceImsObject.resource, Object.assign({}, preRequestResource));
            // Process the response and run it through the collection
            const changed = this._processResources([resultResource], false, true, new Date());

            if (deleted) {
                // HACK fix for deployment.
                if (preRequestResource.id in changed) {
                    changed[preRequestResource.id].removed = true;
                }
            }

            // Handle the case where a locally, created while offline object,
            // gets saved and turns into a real persisted object with a
            // positive id.
            if (preRequestResource.id < 0 && resultResource.id > 0) {
                await this.localObjToPersistentObj(
                    preRequestResource.id,
                    resultResource.id,
                    changed[resultResource.id],
                );
            }

            this.collectionChanged(changed, this.constructor.name);

            // Return the object from the changed object
            return {
                // success or queued...
                resultCode: crudRunnerResult.resultCode,
                jobType: crudRunnerResult.jobType,
                obj: changed[resultResource.id],
            };
        } catch (error) {
            if (_.isNil(error.status)) {
                // It isn't an api error, probably a programming error.
                console.error(error);
                throw error;
            } else {
                // it' an api error
                throw createErrorFromRequestFailure('Error from crud operation _crudAction', error);
            }
        }
    }

    /**
     1. remove local object from collection
     2. emit event
     I don't recommend changing this order. The intent is for the rest of the
     application to listen to the event and change any ids that they may hold.
     * @param localId
     * @param apiId
     * @param newObj
     * @private
     */
    private async localObjToPersistentObj(localId: number, apiId: number, newObj: ImsObject) {
        if (localId > 0) {
            throw new Error(`Expected id to be less than 0`);
        }
        if (!newObj) {
            throw new Error(`Expect newObj to be truthy`);
        }

        let localObj;
        try {
            localObj = this.get(localId);
        } catch (LocalIMSObjectNotFound) {
            // Most likely: The store and forwarder CRUD Runner
            // assigned an object ID but the object didn't make it to the collection,
            // most likely due to an error in the form.
            return;
        }

        if (localObj) {
            // TODO: make localObj.imsType better
            await this._localToPersistentObjEventRegistry.notifyListeners(
                localId,
                apiId,
                newObj.imsType,
            );

            // Signal to IMS widgets that this needs to be removed
            localObj.removed = true;
            // Signal to PersistedCache classes that this needs to be removed
            localObj.resource.is_deleted = true;
            // Send out the notifications!
            this.collectionChanged({ localId: localObj }, this.constructor.name);
            delete this.all[localId];
        }
    }

    public addLocalToPersistentObjListener(
        callback: (localId: number, apiId: number, imsType: string) => void,
    ): () => void {
        return this._localToPersistentObjEventRegistry.addEventListener(callback);
    }

    /**
     * Temporary public method. Will probably get rid of _crudAction later
     *
     * @param promise Promise of the result of a crud action with the API
     * @param preRequestSourceImsObject An ims object representative of just before the
     *        api call
     * @param preRequestResource A copy of the resource just before the api call
     * @param {any} deleted
     * @returns {any}
     */
    async processCrudResponse(
        promise: Promise<CRUDRunnerResult>,
        preRequestSourceImsObject,
        preRequestResource,
        deleted = undefined,
    ): Promise<any> {
        return await this._crudAction(
            promise,
            preRequestSourceImsObject,
            preRequestResource,
            deleted,
        );
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection.defineRelationship
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Define a relationships
     */
    defineRelationship(toKey, externalForeignKey, internalForeignKey, fromCollection) {
        if (externalForeignKey && internalForeignKey) {
            throw new Error(
                'Relationships with both an internal and external key are deprecated and no longer work',
            );
        }

        if (!fromCollection) {
            fromCollection = 'self';
        }

        if (this._listeners[fromCollection.constructor.name]) {
            return;
        }

        log(
            'Created listener from %c' +
                this.constructor.name +
                '%c to %c' +
                fromCollection.constructor.name +
                '%c for %c' +
                toKey,
            'color: #009688',
            'color: black',
            'color: #009688',
            'color: black',
            'color: #009688',
        );

        this._listeners[fromCollection.constructor.name] = {};
        this._listeners[fromCollection.constructor.name].toKey = toKey;
        this._listeners[fromCollection.constructor.name].externalForeignKey = externalForeignKey;
        this._listeners[fromCollection.constructor.name].internalForeignKey = internalForeignKey;
        this._listeners[fromCollection.constructor.name].fromCollection = fromCollection;

        /*
         If a fromCollection wasn't provided, assume we're building an internal relationship (or a nested tree) between ims objects.
         This is possible through the _buildExternalForeignRelationship function.
         Obviously an actual listener doesn't need to be attached for another collection, so don't add anything to root scope.
         */
        if (fromCollection == 'self') {
            return;
        }

        this._listeners[fromCollection.constructor.name].deconstructor =
            fromCollection.addOnChangedListener(
                this._getProcessListenedToCollectionChangedEventCallback(fromCollection),
            );
    }

    /**
     * Create a <Collection>:Changed event handler for listener registration
     * @param fromCollection
     * @returns {function(*, *=, *=)} The callback function
     * @private
     */
    _getProcessListenedToCollectionChangedEventCallback(fromCollection) {
        return (changed, origin) => {
            // If this collection is empty, there's no point in processing (it likely hasn't broadcast yet)
            if (!this._broadcastWhenEmpty && !Object.keys(this.all).length) {
                return;
            }

            // Grab the listener for this collection change
            const listener = this._listeners[fromCollection.constructor.name];

            let touched = {};

            /*
             Example 1. AssetCollection (this) just heard a change from IpDeviceCollection.
             The listener knows it has a external foreign key (each ip device has asset_id field)
             Build relationships by looping through only the changed ip devices.

             Example 2. AssetCollection (this) just heard a change from AssetTypeCollection.
             This listener doesn't have an external foreign key (the relationship is only in each asset having type_id field)
             Build relationships by looping through entire AssetCollection.
             This is a lot slower in this situation.
             However, hopefully this is unlikely to happen often (such as the example used, AssetTypes don't change that often)
             */

            if (listener.externalForeignKey) {
                log(
                    '%c\t' +
                        this.constructor.name +
                        '%c is building %c' +
                        Object.keys(changed).length +
                        '%c external relationships with %c' +
                        fromCollection.constructor.name,
                    'color: #009688',
                    'color: black',
                    'color: #009688',
                    'color: black',
                    'color: #009688',
                );

                // Build any new relationships, by looping through only the changed objects
                touched = this._buildExternalForiegnRelationship(
                    listener.toKey,
                    listener.externalForeignKey,
                    this.all,
                    changed,
                );
            } else {
                if (!this._broadcastWhenEmpty && !Object.keys(changed).length) {
                    return;
                }

                log(
                    '%c\t' +
                        this.constructor.name +
                        '%c is building %c' +
                        Object.keys(changed).length +
                        '%c internal relationships with %c' +
                        fromCollection.constructor.name,
                    'color: #009688',
                    'color: black',
                    'color: #009688',
                    'color: black',
                    'color: #009688',
                );

                // Build any new relationships by looping through the entire collection.
                touched = this._buildInternalForeignRelationship(
                    listener.toKey,
                    listener.internalForeignKey,
                    this.all,
                    listener.fromCollection.all,
                );
            }
            // Emit that the collection has changed with only the touched objects, and pass through the origin
            this.collectionChanged(touched, origin);
        };
    }

    _emitChanged(changed: { [key: number]: ImsObject }, origin) {
        this._metrics.measure(
            `${this.constructor.name}_collection_change`,
            this._collectionChangedEventRegistry.notifyListeners(changed, origin),
        );
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection._buildExternalForiegnRelationship
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Use this function when ANOTHER collection changes as it will have the most recent references to it's external foreign key
     */
    _buildExternalForiegnRelationship(toKey, fromKey, toObjects, fromObjects) {
        log('\t\tBuilding external foreign relation from ' + fromKey + ' to ' + toKey);
        const _t = window.performance.now();
        const touched = {};

        const fromKeys = Object.keys(fromObjects); // Function called as ('ip_device_id', 'radios', {Radio, Radio}, {IpDevice, IpDevice})
        for (let i = 0; i < fromKeys.length; i++) {
            // Loop through each Radio
            const fromObject = fromObjects[fromKeys[i]]; // Grab each Radio
            const toObject = toObjects[fromObject.resource[fromKey]]; // Grab it's IPDevice

            const previousKey = fromObject['_last_' + fromKey]; // See if there's a _last_ip_device_id in the Radio
            if (
                previousKey &&
                toObjects[previousKey] &&
                previousKey != fromObject.resource[fromKey]
            ) {
                // See if that's different to the current one
                if (toObjects[previousKey][toKey][fromObject.resource.id]) {
                    // See if that's an outdated relation in the IpDevice
                    delete toObjects[previousKey][toKey][fromObject.resource.id]; // If so, delete it
                    touched[toObjects[previousKey].resource.id] = toObjects[previousKey]; // Add it to the touched object
                }
            }

            if (!fromObject.removed) {
                // If the Radio wasn't removed
                if (toObject && !toObject.removed) {
                    // And the IPDevice wasn't removed
                    toObject[toKey][fromObject.resource.id] = fromObject; // Build the relation
                    touched[toObject.resource.id] = toObject; // Add it to the touched object
                }
            } else {
                // The Radio was removed
                if (toObject && !toObject.removed) {
                    // If the IPDevice wasn't removed
                    delete toObject[toKey][fromObject.resource.id]; // Delete the Radio from the IPDevice
                    touched[toObject.resource.id] = toObject; // Add it to the touched object
                }
            }
        }

        log(
            '\t\tProcessed ' +
                Object.keys(fromObjects).length +
                ' objects in ' +
                (window.performance.now() - _t).toFixed(3) +
                ' milliseconds',
        );
        return touched;
    }

    /**
     * @ngdoc method
     * @name ImsObjectCollection._buildInternalForeignRelationship
     * @propertyOf ims.shared.data.service:ImsObjectCollection
     * @description
     * Use this function when THIS collection changes, because it has the most recent references to it's internal foreign key
     */
    _buildInternalForeignRelationship(
        toKey,
        fromKey,
        toObjects,
        fromObjects,
        removeExistingRelationships = undefined,
    ) {
        log('\t\tBuilding internal foreign relation from ' + fromKey + ' to ' + toKey);
        const _t = window.performance.now();
        const touched = {};

        const toKeys = Object.keys(toObjects);
        for (let i = 0; i < toKeys.length; i++) {
            // Function called as ('ip_devices', 'ipDevices', {IpDevice, IpDevice}, {Asset, Asset})
            const toObject = toObjects[toKeys[i]]; // Grab each Asset
            const fromKeys = toObject.resource[fromKey]; // Grab the Internal Foreign Key
            if (!fromKeys) {
                continue;
            } // Check if it even exists first
            if (_.isArray(fromKeys)) {
                // The IFK is an array, it's a OneToMany

                // This if statement catches a very specific case;
                // If we updating an internal relationship we dont want to delete unless we are comparing a single changed parent.
                // Previously, if we updated against a single changed child we would lose all other parent relationships
                if (removeExistingRelationships) {
                    toObject[toKey] = {}; // Clear the toKey array first
                }

                for (let j = 0; j < fromKeys.length; j++) {
                    // Go through each Asset's 'ip_devices'
                    const fromObject = fromObjects[fromKeys[j]]; // Grab that IPDevice

                    // Some explanation inline to the right ->                              // If there is no fromObject we don't do anything to the target
                    //                                                                      // IMS object. This should be ok since a removed fromObject is indicated
                    // by the presence of the 'removed' property.
                    if (fromObject !== undefined) {
                        if (!fromObject.removed) {
                            // If the IpDevice is alive
                            toObject[toKey][fromObject.resource.id] = fromObject; // Build the relation
                            touched[toObject.resource.id] = toObject; // Add it to the touched group
                        } else {
                            // The IPDevice has been remove/doesn't exist
                            if (toObject[toKey][fromKeys[j]]) {
                                // If the relation is already there
                                delete toObject[toKey][fromKeys[j]]; // Delete the IPDevice from the Asset
                                touched[toObject.resource.id] = toObject; // Add it to the touched group
                            }
                        }
                    }
                }
            } else {
                // The IFK isn't an array, it's a ManyToOne or a OneToOne
                const fromObject = fromObjects[fromKeys];
                if (fromObject) {
                    // Only remove an object if it specifically has the removed property set to true
                    if (fromObject.removed) {
                        // The IPDevice has been remove/doesn't exist
                        if (toObject[toKey]) {
                            // If the relation is already there
                            delete toObject[toKey]; // Delete the IPDevice from the Asset
                            touched[toObject.resource.id] = toObject; // Add it to the touched group
                        }
                    } else {
                        toObject[toKey] = fromObject; // Build the relation
                        touched[toObject.resource.id] = toObject; // Add it to the touched group
                    }
                }
            }
        }

        log(
            '\t\tProcessed ' +
                Object.keys(toObjects).length +
                ' objects in ' +
                (window.performance.now() - _t).toFixed(3) +
                ' milliseconds',
        );
        return touched;
    }

    private _getLocalStorageNextLocalObjIdKey(): string {
        return `${this.constructor.name}_next_local_obj_id`;
    }
}

function createErrorFromRequestFailure(msg: string, error) {
    const err = new Error(msg);
    (err as any).data = error.data;
    // Make the response status code available
    (err as any).status = error.status;
    return err;
}
